// Copyright (c) Bentley Systems
import * as React from "react";

export type EtreeActionContextValue = {
  actionPendingNodes: Record<string, boolean>;
  setNodePending: (nodeId: string, pending: boolean) => void;
  clearPendingNodes: () => void;
};

const EtreeActionContext = React.createContext<EtreeActionContextValue | undefined>(
  undefined
);

export const EtreeActionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [actionPendingNodes, setActionPendingNodes] = React.useState<
    Record<string, boolean>
  >({});

  const setNodePending = React.useCallback((nodeId: string, pending: boolean) => {
    setActionPendingNodes((prev) => {
      if (pending) {
        return { ...prev, [nodeId]: true };
      }

      if (!prev[nodeId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
  }, []);

  const clearPendingNodes = React.useCallback(() => {
    setActionPendingNodes({});
  }, []);

  const contextValue = React.useMemo(
    () => ({ actionPendingNodes, setNodePending, clearPendingNodes }),
    [actionPendingNodes, setNodePending, clearPendingNodes]
  );

  return (
    <EtreeActionContext.Provider value={contextValue}>
      {children}
    </EtreeActionContext.Provider>
  );
};

export const useEtreeActionContext = (): EtreeActionContextValue => {
  const context = React.useContext(EtreeActionContext);
  if (!context) {
    throw new Error("useEtreeActionContext must be used within EtreeActionProvider");
  }

  return context;
};
